import type { PolicyDocument, PolicySection } from './types'
import { OPERATOR } from './operator'
import {
  CHILDREN,
  DISPOSAL,
  OFFICER,
  OVERSEAS_HEADERS,
  REMEDIES,
  RETENTION_HEADERS,
  SECURITY,
  changes,
  noAutomaticCollection,
  rights,
  table,
} from './common'

/**
 * 완전 오프라인 게임(수집 0)의 공통 방침 골격.
 *
 * **현재 사용처 없음 (2026-08-25).** 이 골격은 하루에 두 번 자리를 옮겼다 — 2026-08-24 에
 * 오마주 3종이 `game-ads.ts` 로 떠나 비었고, 2026-08-25 아침 소울웰드가 잠깐 썼다가
 * (오프라인 유료 단품 가설) 같은 날 무료+광고로 판정이 뒤집히며 다시 떠났다.
 *
 * **그 두 번이 같은 교훈이다: 이 골격의 수명은 수익화 판정에 매여 있다.** 아래 문장들은
 * 광고 한 줄, 결제 한 줄이면 그 자리에서 거짓이 된다. 파일은 남긴다 — 정말로 수집 0 인
 * 게임이 나오면 그때 이 골격이 맞는 문서다. 다만 **고를 때는 수익화가 확정된 뒤에 고른다.**
 *
 * game/ 트랙의 Godot 게임은 계정·서버·분석 SDK·광고 SDK 가 전부 없고 INTERNET 권한도
 * 요청하지 않는다 — 저장은 기기 안 SaveStore 뿐이다. 아홉 개 방침과 달리 Crashlytics
 * 행조차 사실이 아니므로, "아무것도 기기를 떠나지 않는다"를 그대로 고지한다.
 *
 * **광고·결제·문의 화면을 붙이는 날에는 그 코드와 같은 커밋에서 이 골격 사용을 중단하고
 * 개별 방침으로 갈아타야 한다** (docs/privacy-policy.md §절대 규칙). 세 게임이 이 파일
 * 하나를 쓰는 이유는 지금 사실이 세 앱에서 동일해서다 — 사실이 갈라지면 파일도 가른다.
 */

export function makeOfflineGamePolicy(input: {
  slug: string
  title: string
  /** 스토어 표기 이름 (영어) */
  name: string
  /** 게임이 기기에 저장하는 것 — 사실 그대로 나열 */
  storedOnDevice: string
  effectiveDate: string
  history: readonly string[]
}): PolicyDocument {
  const SECTIONS: readonly PolicySection[] = [
    {
      heading: 'Article 1. Purposes of processing personal data',
      paragraphs: [
        `${input.name} is built to work entirely on your device. The Operator processes no personal data to provide it — there is no account, no server, and the app does not use the network.`,
      ],
    },
    {
      heading: 'Article 2. How the service is built — everything stays on your device',
      paragraphs: [
        `${input.name} works without an account. ${input.storedOnDevice} All of it is stored and processed on your device alone; none of it is transmitted to the Operator or to anyone else.`,
        'The app contains no analytics, no crash reporting, no advertising SDK, and no payment feature, and it does not request network access. Nothing you do in the game leaves your device.',
      ],
    },
    {
      heading: 'Article 3. Personal data processed',
      paragraphs: ['The Operator collects and processes no personal data through this service.'],
      bullets: [
        `[Not collected] ${input.storedOnDevice} These stay on your device only and are not personal data held by the Operator`,
      ],
      note: 'No account, name, email address, phone number, location data, contacts, advertising identifier, or device identifier is collected. Neither sensitive data under Article 23 nor unique identifying information under Article 24 of the Personal Information Protection Act is collected.',
    },
    {
      heading: 'Article 4. Use of device permissions',
      paragraphs: ['The app requests no permission at all — no camera, photos, location, contacts, notifications, or network.'],
    },
    {
      heading: 'Article 5. Processing and retention periods',
      table: table(RETENTION_HEADERS, [
        [
          'Game progress, records, and app settings',
          'On your device only (not held by the Operator)',
          'Not applicable',
        ],
      ]),
      note: 'What is on your device disappears when you delete the app (or reset its data in system settings). The Operator holds nothing, so there is nothing for the Operator to retain or destroy under any statute.',
    },
    {
      heading: 'Article 6. Provision of personal data to third parties',
      paragraphs: [
        'The Operator holds no personal data from this service and provides none to any third party. Personal data is never sold.',
      ],
    },
    {
      heading: 'Article 7. Entrustment of personal data processing',
      paragraphs: [
        'The Operator entrusts no personal data processing for this service — there is no processor, because nothing is collected.',
      ],
    },
    {
      heading: 'Article 8. Transfer of personal data abroad',
      paragraphs: [
        'No personal data is transferred abroad — the service transmits nothing off your device, so there is no recipient, country, or transferred item to list.',
      ],
      table: table(OVERSEAS_HEADERS, []),
      note: 'Because no transfer takes place, there is nothing you need to refuse. Should any transfer abroad ever be introduced, this policy will first be revised to state the recipient, country, items, time and method, purpose, retention period, and how to refuse the transfer, as required by Article 28-8 of the Personal Information Protection Act.',
    },
    DISPOSAL,
    rights(
      [
        'In the app — game progress and settings can be reset from the in-game settings, and deleting the app removes everything that was on your device',
      ],
      'nothing — the Operator holds no personal data from this service',
    ),
    noAutomaticCollection(
      'The service shows no advertising and uses no cookie, advertising identifier, web beacon, or any other automatic collection device.',
    ),
    CHILDREN,
    SECURITY,
    OFFICER,
    REMEDIES,
    changes(input.effectiveDate, input.history),
  ]

  return {
    slug: input.slug,
    title: input.title,
    intro: `${OPERATOR}, establishes and publishes the following privacy policy for ${input.name} (“the service”) under Article 30 of the Personal Information Protection Act of the Republic of Korea, in order to protect the personal data of its users and to deal promptly and smoothly with any grievance arising from it.`,
    effectiveDate: input.effectiveDate,
    lastRevisedDate: input.effectiveDate,
    sections: SECTIONS,
  }
}
